import "./CheckinCardLong.scss";
import { format, differenceInDays, add } from 'date-fns'
import { JourneyApiClient } from "../../utils/JourneyApiClient";
import { useContext, useEffect, useState} from "react";
import Great from "../../assets/images/great.svg";
import Good from "../../assets/images/good.svg";
import Okay from "../../assets/images/okay.svg";
import Bad from "../../assets/images/bad.svg";
import Check from "../../assets/images/check.svg";
import useIsComponentVisible from "../CustomHooks/useIsComponentVisible";
import { ApplicationContext } from '../../misc/ApplicationContext';
import AnalyticsService from '../../misc/AnalyticsService';
import {useHistory, useLocation} from "react-router-dom";
import {getTranslatedGreeting, HOME_PATH} from "../../utils/utils";
import { useTranslation } from 'react-i18next';
import { LocalizedImage } from "../LocalizedImage/LocalizedImage";



export type CheckinGraphProps = {
    className?: string;
    onCheckin?: (state: CheckinState, feelingRating?: number) => void;
    openLoginModal?: () => void;
    userName?: string;
}

export type CheckinState = 'checkedin' | 'not-checkedin' | 'checkedin-previously';

type CheckinDataNode = {
    label: string;
    value: number | null;
    previousValue?: number | null;
}


export function CheckinCardLong(props: CheckinGraphProps) {

    const [checkins, setCheckins] = useState<Array<CheckinDataNode>>([]);
    const [checkedInToday, setCheckedInToday] = useState<boolean>(false);
    const isComponentVisible = useIsComponentVisible();
    const {appResumeCounter, currentUser} = useContext(ApplicationContext);
    const [loading, setLoading] = useState<boolean>(true);
    const history = useHistory();
    const location = useLocation();
    const { t } = useTranslation();
    const greeting = getTranslatedGreeting(currentUser, t);
    const fallbackImage = "https://journeylive.imgix.net/production/daily-practice-image-en.png?fm=webp";
    

    async function fetchLastThirtyDayCheckin() {
        const response = await JourneyApiClient.getLastSelectedDailyCheckins(15);
        const firstNonNullIndex = response.findIndex(r => !!r);
        const responseToShow = response.slice(Math.min(firstNonNullIndex, 15));
        const checkinDataNodes: Array<CheckinDataNode> = responseToShow.map(checkin => ({ label: checkin?.createdAt ? format(new Date(checkin.createdAt), 'MM/dd') : '', value: checkin?.feelingRating ?? null}));
        if (responseToShow[0]) {
            checkinDataNodes[0].label = `${t(`${(differenceInDays(new Date(), new Date(responseToShow[0].createdAt!)) + 1)}-days-ago`)}`
        }


        checkinDataNodes[checkinDataNodes.length - 1].label = `${t('today')}`;
        setCheckins(checkinDataNodes);
        const initiallyCheckedIn = responseToShow[responseToShow.length - 1];
        setCheckedInToday(initiallyCheckedIn !== null);
        if (props.onCheckin) {
            if (initiallyCheckedIn) {
                props.onCheckin('checkedin-previously', initiallyCheckedIn.feelingRating!);
            } else {
                props.onCheckin('not-checkedin');
            }
        }
        setLoading(false);
    }

    /**
     * IF the URL contains the 'auto_checkin=N' query parameter, then issue a checkin and clear the query string
     */
    async function processAutoCheckinUrl() {
        let autoChecking = new URLSearchParams(history.location.search.split('?').pop()).get("auto_checkin");
        const autoCheckingValue = parseInt(autoChecking || "", 10);
        if(!isNaN(autoCheckingValue)) {
            setLoading(true);
            try {
                await checkin(autoCheckingValue);
            } catch {
                // Do nothing
            }
            setLoading(false);
            history.replace(HOME_PATH);
        }
    }

    async function checkin(feelingRating: number) {
        if(!currentUser && props.openLoginModal){
            props.openLoginModal()
        } else {
            try {
                let timeOffsetInMilliseconds = (new Date().getTimezoneOffset() * 60 * 1000);
                await JourneyApiClient.saveDailyCheckinAnswersV2({
                    feelingRating,
                    otherPriority: null,
                    priorities: null
                }, timeOffsetInMilliseconds);
                const newCheckins = checkins.slice(0);
                newCheckins[newCheckins.length - 1] = {
                    label: `${t('today')}`,
                    value: feelingRating
                };
                setCheckins(newCheckins);
                setCheckedInToday(true);
                AnalyticsService.trackUserAction("hp_checkin", location.pathname,
                    {feeling_rating: feelingRating});
                if (props.onCheckin) {
                    props.onCheckin('checkedin', feelingRating);
                }
            } catch (ex) {

            }
        }
    }

    useEffect(() => {
        if (!isComponentVisible || !currentUser) return;
        fetchLastThirtyDayCheckin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isComponentVisible, appResumeCounter]);

    useEffect(()=> {
        if(!loading && !checkedInToday) {
            processAutoCheckinUrl();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, checkedInToday, appResumeCounter]);


    return(
        <div className="checkin-card-long-component journey-card">
            <LocalizedImage
                className='background-image'
                i18nKey={'checkin-long-card-image'}
                fallback={fallbackImage}
            />

            <div className="box-header">
                {checkedInToday && <div className={`box-number ${checkedInToday ? 'complete' : ''}`}>
                    <img src={Check} alt="Check"/>
                </div>}
                <div className="box-title">
                    <div className="title overline">{t("Daily Check-In")}</div>
                </div>
            </div>
            <div className="tile">
                <div className={`checkin-graph-container ${props.className ?? ''}`}>
                    <div className="checkin-prompt-container">
                        <div className={"checkin-prompt header-5"}>
                            {
                                props.userName ?
                                    <>{greeting} <br /> {t('How are you feeling today?')}</>
                                    :
                                    t("How are you feeling today?")
                            }
                        </div>
                        <div className="checkin-buttons">
                            <button className={"checkin-button body-small-variant"} onClick={() => checkin(5)}>
                                <img className={"checkin-image"} alt="Greate" src={Great}/> {t("Great")}
                            </button>
                            <button className={"checkin-button body-small-variant"} onClick={() => checkin(4)}>
                                <img className={"checkin-image"} alt="Good" src={Good}/> {t("Good")}
                            </button>
                            <button className={"checkin-button body-small-variant"} onClick={() => checkin(3)}>
                                <img className={"checkin-image"} alt="Okay" src={Okay}/> {t("Okay")}
                            </button>
                            <button className={"checkin-button body-small-variant"} onClick={() => checkin(2)}>
                                <img className={"checkin-image"} alt="Bad" src={Bad}/> {t("Bad")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    )
}

